import { createFileRoute } from "@tanstack/react-router"
import { AutomaticInboxStatus } from "../components/runs/automatic-inbox-status"
import { RunsFilters } from "../components/runs/runs-filters"
import { RunsReadinessTiles } from "../components/runs/runs-readiness-tiles"
import { RunsStatusTabs } from "../components/runs/runs-status-tabs"
import { RunsResults } from "../components/runs/runs-results"
import { useRunsFilterOptions } from "../hooks/use-runs-filter-options"
import { useSearchFilter } from "../hooks/use-search-filter"
import {
  clearRunsFilters,
  filterReadyRuns,
  runsSearchSchema,
  type RunsSearch,
} from "../lib/runs-list"
import { useRelistRunInboxStatus, useRuns } from "../services/use-runs"

export function RunsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const updateFilter = useSearchFilter<RunsSearch>(navigate)
  const clear = () => {
    void navigate({ search: clearRunsFilters(search) })
  }
  const query = useRuns({
    status: search.status === "all" ? undefined : search.status,
    marketplace: search.marketplace,
    startedBy: search.startedBy,
    origin: search.origin,
    archived: search.archived === "true" ? "all" : false,
  })
  const inbox = useRelistRunInboxStatus()
  const options = useRunsFilterOptions(search.archived === "true")
  const runs = filterReadyRuns(query.data?.runs ?? [], search.ready)
  const countLabel = `${runs.length} ${runs.length === 1 ? "run" : "runs"}`
  return (
    <main data-runs-page className="min-h-screen px-4 pt-5 pb-24 sm:px-6 sm:pt-6 lg:px-12 lg:pt-10">
      <div className="mx-auto max-w-[1160px] text-[14px] leading-[1.5] antialiased">
        <header data-runs-header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="m-0 text-[24px] leading-[1.2] font-semibold tracking-[-0.025em] sm:text-[28px]">
              Runs
            </h1>

            <p className="mt-1.5 text-muted-foreground">
              Listing work per marketplace, and what each run still needs before export.
            </p>
          </div>

          <span className="text-[12px] text-muted-foreground tabular-nums">
            {query.data ? countLabel : "— runs"}
          </span>
        </header>

        <AutomaticInboxStatus status={inbox.data} isLoading={inbox.isLoading} error={inbox.error} />

        <RunsStatusTabs
          status={search.status}
          counts={query.data?.statusCounts}
          onChange={(value) => updateFilter("status", value)}
        />

        <RunsReadinessTiles
          runs={query.data?.runs}
          selected={search.ready}
          onChange={(value) => updateFilter("ready", value)}
        />

        <section
          data-runs-results
          className="overflow-hidden rounded-xl border border-border bg-card shadow-card"
          id="runs-results"
          role="tabpanel"
          aria-labelledby={`runs-tab-${search.status}`}
        >
          <RunsFilters
            search={search}
            onChange={updateFilter}
            onClear={clear}
            {...options}
            count={query.data ? runs.length : undefined}
          />

          <RunsResults
            runs={runs}
            isLoading={query.isLoading}
            error={query.error}
            showStatus={search.status === "all"}
            onClear={clear}
            onRetry={() => {
              void query.refetch()
            }}
          />
        </section>
      </div>
    </main>
  )
}
export const Route = createFileRoute("/runs/")({
  validateSearch: runsSearchSchema,
  component: RunsPage,
})
